/**
 * ExitPage: 회의 종료 후 퇴장 화면
 * wehago-conference RenewalComponents/Exit 에서 TS 리팩토링
 * 마크업 기준: src/markup/meetingend.html
 *
 * location.state 로 전달받는 데이터:
 * - title: 회의방 이름
 * - roomCreationTime: 회의 시작 시간 (timestamp)
 * - exitTime: 퇴장 시간 (timestamp)
 * - roomId: 회의방 ID (참석자 API 조회용)
 * - isGuest: 게스트 여부
 */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import apiConfig from '@/api/config';
import apiManager from '@/api';
import type { ExitParticipant, ExitState } from '@/types/components';
import { formatDuration, formatMeetingDate } from '@/utils/formatUtils';
import { toAbsoluteProfileUrl } from '@/utils/profileUtils';

// ─── 메인 ────────────────────────────────────────────────────────────────────

const ExitPage: React.FC = () => {
  const location = useLocation();
  const state = (location.state as ExitState) || {};
  const { title, roomCreationTime, exitTime, roomId, isGuest, exitMessage, exitParticipants } = state;

  const [participants, setParticipants] = useState<ExitParticipant[]>(exitParticipants || []);

  const duration = roomCreationTime && exitTime ? formatDuration(exitTime - roomCreationTime) : null;
  const meetingDate = formatMeetingDate(roomCreationTime || 0);
  const participantNames = (() => {
    const names = participants.map((p) => p.name).filter(Boolean);
    if (names.length <= 4) return names.join(', ');
    return names.slice(0, 4).join(', ') + '...';
  })();

  // ── 참석자 목록 fetch (exitParticipants가 없는 경우에만 API 조회) ──
  useEffect(() => {
    if (exitParticipants && exitParticipants.length > 0) return;
    if (!roomId) return;
    const fetchParticipants = async () => {
      try {
        const roomInfo = await apiManager.getVideoRoomInfo(roomId);
        const accessUsers: Array<Record<string, unknown>> =
          (roomInfo as Record<string, unknown>).access_user as Array<Record<string, unknown>> || [];
        const mapped = accessUsers.map((u) => ({
          user: String(u.value || ''),
          name: u.type !== 'portal_id' ? String(u.value || '') : String(u.name || ''),
          imageUrl: toAbsoluteProfileUrl(String(u.profile_url || '')) || '',
          isJoined: false,
          joinOrder: Infinity,
        }));

        try {
          const connecting = isGuest
            ? await apiManager.getConnectingUserUnAuth(roomId)
            : await apiManager.getConnectingUserAuth(roomId);
          const connMap = new Map(
            connecting.map((c: Record<string, unknown>, idx: number) => [String(c.user || c.user_id || ''), { ...c, _joinOrder: idx }]),
          );
          for (const p of mapped) {
            const conn = connMap.get(p.user);
            if (conn) {
              p.isJoined = true;
              p.joinOrder = (conn as Record<string, unknown>)._joinOrder as number;
              connMap.delete(p.user);
            }
          }
          for (const [, c] of connMap) {
            const cu = c as Record<string, unknown>;
            mapped.push({
              user: String(cu.user || cu.user_id || ''),
              name: String(cu.user_name || cu.name || ''),
              imageUrl: toAbsoluteProfileUrl(String(cu.profile_url || '')) || '',
              isJoined: true,
              joinOrder: cu._joinOrder as number,
            });
          }
        } catch { /* connecting user fetch 실패 */ }

        // 참석한 순서대로 정렬 (접속 중인 사용자 우선, 접속 순서 유지)
        mapped.sort((a, b) => {
          if (a.isJoined !== b.isJoined) return a.isJoined ? -1 : 1;
          return a.joinOrder - b.joinOrder;
        });

        setParticipants(
          mapped.map((p) => ({ name: p.name, imageUrl: p.imageUrl || undefined })),
        );
      } catch { /* roomInfo fetch 실패 */ }
    };
    fetchParticipants();
  }, [roomId, isGuest, exitParticipants]);

  const goWehagoMain = () => {
    const portalUrl = apiConfig.portalBaseUrl || 'https://www.wehago.com';
    window.location.href = portalUrl;
  };

  const goMeetingRecord = () => {
    const portalUrl = apiConfig.portalBaseUrl || 'https://www.wehago.com';
    window.location.href = `${portalUrl}/wehagomeet/#/conversation-record?roomId=${encodeURIComponent(roomId ?? '')}`;
  };

  return (
    <div id="wrap" className="rtc rtc_new rtc_new3 wrap notosans_font">
      <div id="header" className="header">
        <div className="rtc_header rtc_header__end notosans_font">
          <div className="main_title">
            <h1>
              <a href="#" onClick={(e) => { e.preventDefault(); goWehagoMain(); }}>
                <img
                  src="https://static.wehago.com/imgs/common/wehago_w.svg"
                  width={109}
                  height={22}
                  alt="WEHAGO"
                />
              </a>
            </h1>
          </div>
          {title && (
            <div className="service_title">
              <h2>{title}</h2>
            </div>
          )}
        </div>
      </div>

      <div className="rtcNew_container rtcNew_container--setting rtcNew_container--end notosans_font">
        <div className="rtcNew_endWrapScroll common_customScrollbar">
          <div className="rtcNew_endWrap">
            <div className="rtcNew_endTop">
              <div className="rtcNew_endTop__profile">
                <ProfileSection participants={participants} />
              </div>
              <div className="rtcNew_endTop__titleInfo">
                {title && <strong className="titleText ellipsis">{title}</strong>}
                <p className="descText">{exitMessage || '회의가 종료되었습니다.'}</p>
                {duration && <p className="timeText">{duration}</p>}
              </div>
            </div>

            <div className="rtcNew_endCon">
              <strong className="rtcNew_endCon__title">회의메모·요약</strong>
              <div className="rtcNew_endCon__descBox">
                {meetingDate && <p>회의일자 : {meetingDate}</p>}
                {participantNames && <p>참석자 : {participantNames}</p>}
              </div>
              <div className="rtcNew_endCon__sum">
                <div className="rtcNew_endCon__sumDimm" style={{bottom:"-20px", paddingBottom:"110px"}}>
                  <p>
                    회의 중 공유된 내용이나 대화 기록을 다시 확인하고,
                    <br />
                    <em>ONE AI</em>로 회의 내용을 요약하여
                    <br />
                    핵심 정보를 빠르게 파악해보세요!
                  </p>
                  <button
                    type="button"
                    className="common_round_btn blue7 sizeBig2 notosans_font"
                    onClick={goMeetingRecord}
                  >
                    회의 기록 보러가기
                  </button>
                </div>
                {/* 하드코딩 목업 요약 내용 */}
                <strong className="rtcNew_endCon__subTitle">1. 주요 논의 내용</strong>
                <p className="st">AI 기반 업무 자동화 방안</p>
                <p>문서 자동 요약 및 일정 관리 기능 도입</p>
                <p>AI 챗봇을 활용한 사내정보 검색 기능 추가</p>
                <p>고객 대응 AI 시스템 도입 검토 (FAQ 자동응답, 상담 지원)</p>
                <p className="st">기술적 검토 필요 사항</p>
                <p>현재 사용중인 업무 플랫폼과 AI 연동 가능 여부</p>
                <p>필요한 데이터 및 보안 문제 검토</p>
                <p>기대효과 및 실무 적용 가능성 분석</p>
                <p>업무 효율성 향상 및 반복 작업 감소</p>
                <p>고객 응대 속도 개선 및 직원 업무 부담 감소</p>
                <strong className="rtcNew_endCon__subTitle">2. 결정 사항 및 해야할 일</strong>
                <p>AI 챗봇을 활용한 사내정보 검색 기능 추가</p>
                <p>고객 대응 AI 시스템 도입 검토 (FAQ 자동응답, 상담 지원)</p>
                <p className="st">기술적 검토 필요 사항</p>
                <p>현재 사용중인 업무 플랫폼과 AI 연동 가능 여부</p>
                <p>필요한 데이터 및 보안 문제 검토</p>
                <p>고객 응대 속도 개선 및 직원 업무 부담 감소</p>
                <strong className="rtcNew_endCon__subTitle">2. 결정 사항 및 해야할 일</strong>
                <p>기술검토 : 개발팀에서 AI 연동 가능성 검토 (담당 : 홍길동)</p>
              </div>
            </div>

            <div className="rtcNew_endFooter">
              <a
                href="https://www.wehago.com/landing/ko/oneai/"
                className="rtcNew_endFooter__link"
                target="_blank"
                rel="noreferrer"
              >
                ONE AI 더 알아보기
                <span className="lunacon lunacon--arrowRight-outlined" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── 프로필 그리드 ───────────────────────────────────────────────────────────

const ProfileSection: React.FC<{ participants: ExitParticipant[] }> = ({ participants }) => {
  const count = participants.length;

  if (count === 0) {
    return <div className="common_newProfile--empty" />;
  }

  if (count === 1) {
    const p = participants[0];
    return p.imageUrl ? (
      <div className="common_newProfile">
        <img src={p.imageUrl} alt={p.name || ''} />
      </div>
    ) : (
      <div className="common_newProfile--empty" />
    );
  }

  const maxCount = Math.min(count, 4);

  return (
    <div className={`common_newProfile--multiTypeWrap has${maxCount}`}>
      {participants.slice(0, maxCount).map((p, i) =>
        p.imageUrl ? (
          <div key={i} className="common_newProfile multiType">
            <img src={p.imageUrl} alt={p.name || ''} />
          </div>
        ) : (
          <div key={i} className="common_newProfile--empty multiType" />
        ),
      )}
    </div>
  );
};

export default ExitPage;
